import { useLang } from "@/context/LanguageContext";
import { Coffee, Sun, Moon, Flame, MoveRight, ExternalLink, Sparkles } from "lucide-react";
import { MENU } from "@/lib/menu";
import { WA_FOOD_MENU } from "@/lib/contact";

const ICONS = { breakfast: Coffee, lunch: Sun, dinner: Moon, bbq: Flame };
const MENU_PDF = "https://customer-assets.emergentagent.com/job_naljuri-riverside/artifacts/6r757791_Foldable%20Food%20Menu.pdf";

export default function Dining() {
  const { lang } = useLang();
  const head = lang === 'en'
    ? { eyebrow: "On-site dining", title: "Hot food, hill-side.", sub: "A home kitchen — every dish is prepared on a pre-order basis. Fixed plates from ৳100. Charcoal BBQ under the stars." }
    : { eyebrow: "অন-সাইট ডাইনিং", title: "পাহাড়ের কোলে গরম খাবার।", sub: "আমাদের নিজস্ব হোম-কিচেন — সব খাবার প্রি-অর্ডারে। ১০০ টাকা থেকে ফিক্সড প্লেট। তারার নিচে কাঠকয়লার বিবিকিউ।" };

  const regular = MENU.sections.filter((s) => !s.featured);
  const featured = MENU.sections.find((s) => s.featured);

  return (
    <section id="dining" className="bg-cream-50 border-y border-forest-100" data-testid="dining-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Header */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="eyebrow tag-line">{head.eyebrow}</p>
            <h2 className={`mt-4 text-4xl md:text-5xl font-display text-forest-700 tracking-tight leading-[1.05] ${lang==='bn'?'font-bn':''}`}
                data-testid="dining-title">
              {head.title}
            </h2>
            <p className={`mt-4 text-base text-slate-warm max-w-xl ${lang==='bn'?'font-bn':''}`}>{head.sub}</p>
            <div className="mt-5 inline-flex items-center gap-2 bg-cream-100 border border-gold-600/40 text-forest-700 px-3.5 py-1.5 rounded-full"
                 data-testid="home-kitchen-badge">
              <span className="live-dot" />
              <span className={`text-[11px] uppercase tracking-widest2 ${lang==='bn'?'font-bn':''}`}>
                {lang === 'en' ? 'Home kitchen · pre-order basis' : 'হোম কিচেন · প্রি-অর্ডারে'}
              </span>
            </div>
            <div className="gold-rule mt-6" />
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex flex-wrap gap-3">
            <a href={WA_FOOD_MENU} target="_blank" rel="noreferrer" data-testid="dining-wa-order"
               className="inline-flex items-center gap-2 bg-forest-700 text-cream-100 px-5 py-2.5 rounded-full text-xs hover:bg-forest-800 transition-colors border border-gold-600/40">
              {lang === 'en' ? 'Order via WhatsApp' : 'হোয়াটসঅ্যাপে অর্ডার'} <MoveRight className="w-3.5 h-3.5" />
            </a>
            <a href={MENU_PDF} target="_blank" rel="noreferrer" data-testid="dining-pdf-link"
               className="inline-flex items-center gap-2 border border-forest-200 text-forest-700 px-5 py-2.5 rounded-full text-xs hover:bg-cream-200 transition-colors">
              {lang === 'en' ? 'Full menu (PDF)' : 'পূর্ণ মেনু (PDF)'} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Three regular meal cards (with hero food photo) */}
        <div className="grid md:grid-cols-3 gap-5">
          {regular.map((s) => <MealCard key={s.key} section={s} lang={lang} />)}
        </div>

        {/* Featured BBQ Night strip — full-bleed */}
        {featured && <BBQHero section={featured} lang={lang} />}

        <p className={`mt-10 text-xs text-slate-warm max-w-2xl ${lang==='bn'?'font-bn':''}`}>
          {lang === 'en'
            ? "All prices in BDT, inclusive. Vegetarian / vegan / Halal options available — please intimate the concierge a meal in advance."
            : "সব দাম বিডিটি, ভ্যাট অন্তর্ভুক্ত। নিরামিষ / ভিগান / হালাল অপশন আছে — অনুগ্রহ করে কনসিয়ার্জকে আগে জানিয়ে রাখুন।"}
        </p>
      </div>
    </section>
  );
}

const MealCard = ({ section, lang }) => {
  const Icon = ICONS[section.key] || Coffee;
  return (
    <article className="group bg-white border border-forest-100 rounded-md flex flex-col overflow-hidden"
             data-testid={`menu-section-${section.key}`}>
      {/* Hero food photo */}
      <div className="relative h-56 overflow-hidden">
        <img src={section.hero_img}
             alt={lang === 'en' ? `${section.name_en} at Naljuri Inn — Jaflong Sylhet luxury resort dining` : `${section.name_bn} — নলজুরি ইন, জাফলং`}
             loading="lazy"
             className="w-full h-full object-cover img-zoom" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900/85 via-forest-900/15 to-transparent" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-cream-100/95 text-forest-700 px-3 py-1.5 rounded-full">
          <Icon className="w-3.5 h-3.5 text-gold-600" />
          <span className="text-[10px] uppercase tracking-widest2 font-mono">{section.time}</span>
        </div>
        <div className="absolute inset-0 p-5 flex flex-col justify-end">
          <h3 className={`text-xl font-display text-cream-100 ${lang==='bn'?'font-bn':''}`}>
            {lang === 'en' ? section.name_en : section.name_bn}
          </h3>
          <p className={`text-xs text-cream-100/80 mt-1 ${lang==='bn'?'font-bn':''}`}>
            {lang === 'en' ? section.caption_en : section.caption_bn}
          </p>
        </div>
      </div>

      {/* Items */}
      <ul className="p-6 flex-1 divide-y divide-forest-100">
        {section.items.map((it, i) => (
          <li key={i} className="py-4 first:pt-0 last:pb-0">
            <div className="flex items-baseline justify-between gap-3">
              <div className={`text-sm text-forest-700 ${lang==='bn'?'font-bn':''}`}>
                {lang === 'en' ? it.name_en : it.name_bn}
              </div>
              <div className="text-sm font-display text-forest-700 shrink-0">
                {it.price_bdt ? `৳${it.price_bdt}` : <span className="text-xs text-slate-warm">à la carte</span>}
              </div>
            </div>
            <p className={`mt-1.5 text-xs text-slate-warm leading-relaxed ${lang==='bn'?'font-bn':''}`}>
              {lang === 'en' ? it.desc_en : it.desc_bn}
            </p>
          </li>
        ))}
      </ul>
    </article>
  );
};

const BBQHero = ({ section, lang }) => {
  const Icon = ICONS[section.key] || Flame;
  return (
    <article className="mt-8 grid lg:grid-cols-12 gap-0 border border-forest-100 rounded-md overflow-hidden bg-forest-900 text-cream-100"
             data-testid={`menu-section-${section.key}`}>
      {/* Hero food photo */}
      <div className="lg:col-span-6 relative min-h-[360px] lg:min-h-full overflow-hidden group">
        <img src={section.hero_img}
             alt={lang === 'en' ? `${section.name_en} — BBQ & riverside grill at Naljuri Inn, Jaflong` : `${section.name_bn} — নলজুরি ইন রিভারসাইড বারবিকিউ`}
             loading="lazy"
             className="absolute inset-0 w-full h-full object-cover img-zoom" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-900/55 to-forest-900/10" />
        <div className="absolute top-5 left-5 inline-flex items-center gap-2 offer-ribbon px-3 py-1.5 rounded-full">
          <Sparkles className="w-3 h-3" />
          <span className="text-[10px] uppercase tracking-widest2">New · Featured</span>
        </div>
      </div>

      {/* Body */}
      <div className="lg:col-span-6 p-8 md:p-12 flex flex-col justify-center">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full border border-gold-600/50 inline-flex items-center justify-center">
            <Icon className="w-4 h-4 text-gold-400" />
          </span>
          <div>
            <div className={`text-2xl md:text-3xl font-display text-cream-100 ${lang==='bn'?'font-bn':''}`}>
              {lang === 'en' ? section.name_en : section.name_bn}
            </div>
            <div className="text-[10px] uppercase tracking-widest2 text-gold-400 mt-1 font-mono">{section.time}</div>
          </div>
        </div>
        <p className={`mt-5 text-base text-cream-100/85 max-w-xl ${lang==='bn'?'font-bn':''}`}>
          {lang === 'en' ? section.caption_en : section.caption_bn}
        </p>

        <div className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-4">
          {section.items.map((it, i) => (
            <div key={i} className="flex items-start justify-between gap-3 border-t border-cream-100/15 pt-3">
              <div className="min-w-0">
                <div className={`text-sm text-cream-100 leading-tight ${lang==='bn'?'font-bn':''}`}>
                  {lang === 'en' ? it.name_en : it.name_bn}
                </div>
                <p className={`mt-1 text-[11px] text-cream-100/70 leading-relaxed ${lang==='bn'?'font-bn':''}`}>
                  {lang === 'en' ? it.desc_en : it.desc_bn}
                </p>
              </div>
              <div className="text-sm font-display text-gold-400 shrink-0">
                {it.price_bdt ? `৳${it.price_bdt}` : '—'}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <a href={`https://wa.me/8801344089779?text=${encodeURIComponent("Hello Naljuri Inn — I'd like to book a BBQ Night.")}`}
             target="_blank" rel="noreferrer"
             data-testid="bbq-reserve"
             className="inline-flex items-center gap-2 bg-gold-600 text-cream-100 px-5 py-2.5 rounded-full text-xs hover:bg-gold-700 transition-colors border border-gold-500/40">
            <Flame className="w-3.5 h-3.5" />
            {lang === 'en' ? 'Reserve a BBQ Night' : 'বিবিকিউ নাইট রিজার্ভ করুন'}
            <MoveRight className="w-3.5 h-3.5" />
          </a>
          <span className={`text-[11px] text-cream-100/65 self-center ${lang==='bn'?'font-bn':''}`}>
            {lang === 'en' ? 'Pre-order 4 hours in advance' : '৪ ঘণ্টা আগে অর্ডার করুন'}
          </span>
        </div>
      </div>
    </article>
  );
};
